import { Tooltip } from '@foam/buoy/src/components/Tooltip/Tooltip';
import { cn } from '@foam/buoy/src/lib/utils';
import React, { useMemo, useRef } from 'react';

import { BrandItem } from './BrandExperience';

interface AutoScrollContainerProps {
  items: BrandItem[];
  className?: string;
}

// Memoized logo component for better performance
const LogoItem = React.memo<{
  item: BrandItem;
  index: number;
  setKey: string;
}>(({ item, index, setKey }) => {
  return (
    <Tooltip
      key={`${setKey}-${index}`}
      content={item.alt}
      data-testid="content-tooltip"
    >
      <img
        src={item.src}
        alt={item.alt}
        className="mx-8 inline max-h-16 max-w-32 object-contain"
        loading="lazy"
        draggable={false}
        style={{
          imageRendering: 'auto',
          backfaceVisibility: 'hidden',
        }}
      />
    </Tooltip>
  );
});

// Memoized logo row component
const LogoRow = React.memo<{
  items: BrandItem[];
  setKey: string;
  className?: string;
}>(({ items, setKey, className }) => (
  <div className={cn('inline-block w-max', className)}>
    {items.map((item, index) => (
      <LogoItem
        key={`${setKey}-${index}`}
        item={item}
        index={index}
        setKey={setKey}
      />
    ))}
  </div>
));

export const AutoScrollContainer = ({
  items,
  className = '',
}: AutoScrollContainerProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const parentContainerRef = useRef<HTMLDivElement>(null);

  const shouldAutoScroll = useMemo(() => {
    if (!scrollContainerRef.current || !parentContainerRef.current)
      return false;

    return (
      scrollContainerRef.current.scrollWidth >
      parentContainerRef.current.clientWidth
    );
  }, [scrollContainerRef.current, parentContainerRef]);

  // Early return for empty items
  if (!items.length) {
    return null;
  }

  return (
    <div
      className="relative overflow-hidden"
      aria-label="Auto-scrolling brand logos"
      ref={parentContainerRef}
    >
      <div
        ref={scrollContainerRef}
        className={cn(
          'group relative overflow-hidden whitespace-nowrap h-40 flex items-center',
          {
            'justify-center': !shouldAutoScroll, // We don't want logos centered when they are scrolling
          },
          className,
        )}
      >
        <LogoRow
          items={items}
          setKey="first"
          className={cn({
            'animate-endless-scroll group-hover:animation-pause':
              shouldAutoScroll,
          })}
        />
        {shouldAutoScroll ? (
          <LogoRow
            items={items}
            setKey="second"
            className="group-hover:animation-pause animate-endless-scroll"
          />
        ) : null}
      </div>
    </div>
  );
};
